'use client'

import { OverlayButton } from '@fukku/button/Overlay/OverlayButton'
import { OverlayButtonVariant } from '@fukku/button/Overlay/types'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useReducedMotion } from 'hooks/useReducedMotion/useReducedMotion'
import { IconPauseL } from 'icons/components/IconPauseL'
import { IconPlayL } from 'icons/components/IconPlayL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useVideoFallbackImage } from 'media/hooks/video/useVideoFallbackImage/useVideoFallbackImage'
import { useVideoUrl } from 'media/hooks/video/useVideoUrl/useVideoUrl'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import { type AnimationEvent, useEffect, useRef, useState } from 'react'
import { isBot } from 'utils/isBot/IsBot'

import styles from './Video.module.css'

interface VideoProps {
	readonly url: string
	readonly className?: string
	readonly tabIndex?: number
}

const ICON_SIZE = 20

export function Video({
	url,
	className,
	tabIndex,
}: VideoProps): React.ReactNode {
	const { isCicd } = useMasterData()
	const { t } = useLabels()
	const reducedMotion = useReducedMotion()
	const {
		sendVideoAutoPauseEvent,
		sendVideoAutoPlayEvent,
		sendVideoEndedEvent,
		sendVideoErrorEvent,
		sendVideoPauseClickEvent,
		sendVideoPlayClickEvent,
		sendVideoViewEvent,
	} = useAnalytics()
	const [isPausedByUser, setIsPausedByUser] = useState(isCicd || reducedMotion)

	const { videoSrc } = useVideoUrl({ src: url })
	const fallbackImageSrc = useVideoFallbackImage({
		videoUrl: videoSrc,
	})

	const videoRef = useRef<HTMLVideoElement | null>(null)
	const iconRef = useRef<HTMLDivElement | null>(null)
	const startedTimeRef = useRef(Date.now())
	const videoEndedTimesRef = useRef(0)

	const entry = useIntersectionObserver(videoRef, {
		threshold: 0.2,
	})

	useEffect(() => {
		if (!isCicd) {
			setIsPausedByUser(reducedMotion)
		}
	}, [reducedMotion])

	const toggle = (): void => {
		if (videoRef.current?.paused) {
			videoRef.current.play()
			sendVideoPlayClickEvent()
		} else {
			videoRef.current?.pause()
			sendVideoPauseClickEvent()
		}
		setIsPausedByUser((prev) => !prev)
	}

	const onEnded = (): void => {
		videoRef.current?.play()
		videoEndedTimesRef.current += 1

		if (videoEndedTimesRef.current === 1) {
			sendVideoEndedEvent()
		}
	}

	const onPlaying = () => {
		if (videoEndedTimesRef.current === 0) {
			sendVideoViewEvent()
		}
	}

	const onError = () => {
		const time = Date.now() - startedTimeRef.current
		sendVideoErrorEvent({ time })
	}

	const onButtonAnimationEnd = (e: AnimationEvent<HTMLDivElement>): void => {
		iconRef.current?.classList.remove(styles.fadeout)

		if (e.animationName === styles['fade-out']) {
			toggle()
			iconRef.current?.classList.add(styles.fadein)
		}
	}

	const addFadeOut = (): void => {
		iconRef.current?.classList.remove(styles.fadein)
		iconRef.current?.classList.add(styles.fadeout)
	}

	useEffect(() => {
		if (videoRef.current) {
			if (entry?.isIntersecting && !isPausedByUser && !isBot()) {
				videoRef.current?.play()
				sendVideoAutoPlayEvent()
			} else if (videoRef.current?.paused) {
				// Do nothing
			} else {
				videoRef.current?.pause()
				sendVideoAutoPauseEvent()
			}
		}
	}, [entry?.isIntersecting])

	return (
		<div className={styles.video}>
			<video
				src={videoSrc}
				poster={fallbackImageSrc}
				onEnded={onEnded}
				onError={onError}
				onPlaying={onPlaying}
				className={className}
				ref={videoRef}
				data-testid={dataTestIds['pdp.gallery.video']}
				muted
				playsInline
			>
				<source src={videoSrc} type='video/mp4' />
				<track kind='captions' />
			</video>
			<div className={styles.buttonWrapper}>
				<OverlayButton
					variant={OverlayButtonVariant.IconMedium}
					onClick={addFadeOut}
					data-testid={
						isPausedByUser
							? dataTestIds['pdp.video.button.play']
							: dataTestIds['pdp.video.button.pause']
					}
					icon={
						<div ref={iconRef} onAnimationEnd={onButtonAnimationEnd}>
							<IconPlayL
								height={ICON_SIZE}
								width={ICON_SIZE}
								className={isPausedByUser ? styles.show : styles.hide}
								alt={t('accessibility.pdp.play.button')}
							/>
							<IconPauseL
								height={ICON_SIZE}
								width={ICON_SIZE}
								className={isPausedByUser ? styles.hide : styles.show}
								alt={t('accessibility.pdp.pause.button')}
							/>
						</div>
					}
					tabIndex={tabIndex}
				/>
			</div>
		</div>
	)
}
